/**
 * Value editor for a single rule condition: dropdown when the field has a
 * known option set, otherwise free text. List operators stay comma-separated text.
 */
import type { CSSProperties } from 'react';
import { Select, TextInput } from '@mantine/core';
import { ORG_TYPE_PAYER } from '@unify/fhir-model/constants';

import type { ConditionField, ConditionOperator } from '../types';
import { getConditionValueSelectOptions } from '../utils/conditionFieldValueOptions';
import { ClaimFhirReferenceSelect } from './ClaimFhirReferenceSelect';

function withLegacyOption(
  base: { value: string; label: string }[],
  current: string,
): { value: string; label: string }[] {
  const t = current.trim();
  if (!t || base.some((o) => o.value === t)) return base;
  return [...base, { value: t, label: t }];
}

export interface ConditionValueInputProps {
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
  onChange: (value: string) => void;
  size?: 'xs' | 'sm' | 'md';
  style?: CSSProperties;
}

export function ConditionValueInput({
  field,
  operator,
  value,
  onChange,
  size = 'xs',
  style,
}: ConditionValueInputProps): JSX.Element | null {
  if (operator === 'exists' || operator === 'not-exists') {
    return null;
  }

  if (operator === 'in-list' || operator === 'not-in-list') {
    return (
      <TextInput
        size={size}
        placeholder="Comma-separated values"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        style={style}
      />
    );
  }

  if (field === 'coverage.payorId') {
    return (
      <div style={style}>
        <ClaimFhirReferenceSelect
          resourceType="Organization"
          value={value}
          persistFullReference={false}
          searchType={ORG_TYPE_PAYER}
          onChange={onChange}
        />
      </div>
    );
  }

  if (field === 'location.id') {
    return (
      <div style={style}>
        <ClaimFhirReferenceSelect
          resourceType="Location"
          value={value}
          persistFullReference={false}
          onChange={onChange}
        />
      </div>
    );
  }

  const baseOptions = getConditionValueSelectOptions(field, operator);
  if (baseOptions) {
    const data = withLegacyOption(baseOptions, value);
    return (
      <Select
        size={size}
        placeholder="Value"
        searchable
        clearable
        data={data}
        value={value || null}
        onChange={(v) => onChange(v ?? '')}
        style={style}
      />
    );
  }

  return (
    <TextInput
      size={size}
      placeholder="Value"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      style={style}
    />
  );
}
